<template>
  <div class="autocomplete-input input">
    <div class="input-wrapper" ref="wrapper">
      <text-input
        :disabled="disabled"
        :dynamic-label="dynamicLabel"
        :label="label"
        :placeholder="placeholder"
        v-model="term"
        select-on-focus
        @focus="() => initToggleActive(true)"
        @keyup="handleKeyUp"
      />

      <div class="items-holder" :class="{ active }" ref="holderRef">
        <ul class="items">
          <li
            v-for="(item, i) in filteredItems"
            class="item"
            :class="{ selected: i === selectedItem }"
            :key="item.value"
            @click="select(item.value)"
            ref="itemsRef"
          >
            {{ item.text }}
          </li>
        </ul>
      </div>
    </div>

    <div class="selected-items" v-if="multiple">
      <div class="item" v-for="item in itemsList" :key="item">
        <span>{{ getItemText(item) }}</span>
        <div class="remove" @click="select(item)">
          <font-awesome-icon class="icon" :icon="faTimes" />
        </div>
      </div>
    </div>

    <small class="red" v-if="!!validateMessage">{{ validateMessage }}</small>
  </div>
</template>

<script>
import { ref, computed, watch } from '@vue/composition-api';
import $ from 'jquery';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';

import { normalizeText } from '@/helpers';

import TextInput from '@/components/TextInput';

export default {
  name: 'AutocompleteInput',
  props: {
    placeholder: String,
    label: String,
    dynamicLabel: Boolean,
    items: {
      type: Array,
      default: () => []
    },
    value: {
      type: [Number, String, Array],
      default: null
    },
    multiple: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    validate: {
      type: Function,
      default: () => ''
    },
    disabled: {
      type: Boolean,
      default: false,
    }
  },
  components: {
    TextInput,
    FontAwesomeIcon
  },
  setup(props, { emit, root }) {
    const term = ref('');
    const active = ref(false);
    const wrapper = ref(null);
    const items = computed(() => props.items);
    const filteredItems = ref(props.items);
    const selectedItem = ref(-1);
    const itemsRef = ref(null);
    const holderRef = ref(null);
    const validateMessage = ref('');
    const isValid = ref(false);

    const wrappedValue = computed({
      get() { return props.value; },
      set(val) {
        emit('input', val);
      }
    });

    const parseValueToArray = () => {
      let val;
      if (wrappedValue.value) {
        if (typeof wrappedValue.value === 'string') {
          val = [wrappedValue.value];
        } else {
          if (
            (typeof wrappedValue.value === 'object')
            && Object.prototype.hasOwnProperty.call(wrappedValue.value, 'length')
          ) {
            val = wrappedValue.value;
          } else {
            val = [wrappedValue.value];
          }
        }
      } else {
        val = [];
      }

      return val;
    };

    const itemsList = computed(() => parseValueToArray());

    const toggleActive = (val) => {
      if (typeof val === 'boolean') {
        active.value = val;
      } else {
        active.value = !active.value;
      }
    };

    const initToggleActive = (val) => {
      filteredItems.value = [...items.value];
      toggleActive(val);
    };

    const watchWrapper = (e) => {
      const elem = wrapper.value;
      if (!elem) return;

      let { pageX, pageY } = e;
      let { top, left } = $(elem).offset();
      let width = $(elem).width();
      let height = $(elem).height();

      if ((pageX < left) || (pageX > left + width) || (pageY < top) || (pageY > top + height)) {
        ({ pageX, pageY } = e);
        ({ top, left } = $(elem).offset());
        width = $(elem).outerWidth();
        height = $(elem).outerHeight();

        if ((pageX < left) || (pageX > left + width) || (pageY < top) || (pageY > top + height)) {
          toggleActive(false);
        }
      }
    };

    const getItemText = (val) => {
      const item = props.items.find((i) => i.value === val);
      if (item) {
        return item.text;
      }

      return '';
    };

    const select = (val) => {
      if (props.multiple) {
        if (
          (typeof wrappedValue.value === 'object')
          && Object.prototype.hasOwnProperty.call(wrappedValue.value, 'length')
        ) {
          const i = wrappedValue.value.findIndex((v) => (v === val));

          if (i < 0) {
            wrappedValue.value.push(val);
          } else {
            wrappedValue.value = [
              ...wrappedValue.value.slice(0, i),
              ...wrappedValue.value.slice(i + 1, wrappedValue.value.length),
            ];
          }
        } else {
          wrappedValue.value = [val];
        }
      } else {
        wrappedValue.value = val;
      }

      active.value = false;
    };

    const handleKeyUp = (e) => {
      const { keyCode } = e;

      if (!itemsRef.value || !itemsRef.value.length) return;

      switch (keyCode) {
        case 38:
          selectedItem.value -= 1;
          if (selectedItem.value < 0) selectedItem.value = 0;

          if (holderRef.value.scrollTop > itemsRef.value[selectedItem.value].offsetTop) {
            holderRef.value.scrollTop = itemsRef.value[selectedItem.value].offsetTop;
          }

          break;

        case 40:
          selectedItem.value += 1;
          if (selectedItem.value > filteredItems.value.length - 1) selectedItem.value = filteredItems.value.length - 1;

          if (holderRef.value.scrollTop + $(holderRef.value).height() - $(itemsRef.value[0]).height() < itemsRef.value[selectedItem.value].offsetTop) {
            holderRef.value.scrollTop = itemsRef.value[selectedItem.value - 1].offsetTop;
          }

          break;

        case 13:
          if ((selectedItem.value >= 0) && (selectedItem.value < filteredItems.value.length)) {
            select(filteredItems.value[selectedItem.value].value);
          }
          break;

        default:
          break;
      }
    };

    const alert = (msg) => {
      validateMessage.value = msg;
    };

    const validateInput = () => {
      const vals = parseValueToArray();

      if (props.required && !vals.length) {
        alert(root.$t('REQUIRED_FIELD'));
        return false;
      }

      const msg = props.validate(wrappedValue.value);

      if (msg) {
        alert(root.$t(msg));
        return false;
      }

      alert('');
      return true;
    };

    const magnetValue = () => {
      if (!props.multiple) {
        term.value = getItemText(wrappedValue.value);
      }
    };

    watch(active, (next) => {
      if (next) {
        setTimeout(() => {
          $(document.body).on('click', watchWrapper);
        }, 0);
      } else {
        $(document.body).off('click', watchWrapper);
        magnetValue();
      }
    });

    watch([term, itemsList, items], ([val, selectedList, list_]) => {
      let list = [...list_];

      if (props.multiple) {
        list = list.filter((item) => (selectedList.indexOf(item.value) < 0));
      }

      if (val) {
        list = list.filter((item) => normalizeText(item.text).indexOf(normalizeText(val)) >= 0);
      }

      filteredItems.value = list;
      selectedItem.value = -1;
      holderRef.value.scrollTop = 0;
    });

    watch(wrappedValue, (val) => {
      term.value = getItemText(val);
    });

    return {
      term,
      itemsList,
      active,
      initToggleActive,
      wrapper,
      filteredItems,
      handleKeyUp,
      selectedItem,
      select,
      getItemText,
      faTimes,
      itemsRef,
      holderRef,
      validateInput,
      validateMessage,
      isValid
    };
  }
};
</script>

<style lang="scss">
@import '~@/assets/scss/colors';

.autocomplete-input {
  position: relative;

  .items-holder {
    position: absolute;
    top: 40px;
    left: 0;
    width: 100%;
    max-height: 0;
    background-color: $babyPowder;
    overflow-y: scroll;
    z-index: 10;
    box-shadow:inset 0px 0px 0px 1px $alto;
    transition: all .2s ease-in-out;
    box-sizing: border-box;

    ul {
      list-style: none;
      padding: 0;
      margin: 0;

      > li {
        padding: 7px 15px;
        transition: all .2s ease-in-out;

        &:hover, &.selected {
          background-color: $independence;
          color: $babyPowder;
          cursor: pointer;
        }
      }
    }

    &.active {
      max-height: 200px;
    }
  }

  .selected-items {
    margin-top: 5px;

    .item {
      display: inline-block;
      border: 1px solid $alto;
      border-radius: 20px;
      padding: 3px 7px;
      padding-right: 25px;
      font-size: 12px;
      margin-right: 5px;
      margin-bottom: 5px;
      position: relative;

      .remove {
        position: absolute;
        top: 3.5px;
        right: 9px;
        cursor: pointer;
      }
    }
  }

  small {
    &.red {
      color: $geranium;
    }
  }
}
</style>
